import Component from '@glimmer/component';
import ThickArrowDownIcon from './icons/thick-arrow-down.gts';
import ArrowDownIcon from './icons/arrow-down.gts';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';

export interface S {
  Element: HTMLDetailsElement;
  Args: {
    summary?: string;
    disabled?: boolean;
    color?: 'pink' | 'purple' | 'grey';
    level?: 'title' | 'sub-title';
    open?: boolean;
  };
  Blocks: {
    default: [isOpen: WrDetails['isOpen']];
    summary?: [isOpen: WrDetails['isOpen']];
  };
}

const STYLES = {
  base: [
    '[&[open]]:pb-4',

    // summary styles
    '[&>summary]:font-sans',
    '[&>summary]:mb-4',
    '[&>summary]:font-medium',
    '[&>summary]:text-lg',
    '[&>summary]:flex',
    '[&>summary]:gap-2',
    '[&>summary]:justify-start',
    '[&>summary]:items-center',
    '[&>summary]:px-4',
    '[&>summary]:py-2',
    '[&>summary]:rounded-full',
    '[&>summary]:sm:px-44',
    '[&>summary]:sm:py-22',
    '[&>summary::-webkit-details-marker]:hidden',

    // icon styles
    '[&[open]>summary>svg]:rotate-0'
  ],
  color: {
    pink: [
      '[&>summary]:text-pink',
      '[&>summary]:bg-pink-light',
      '[&>summary:focus-visible]:outline-pink'
    ],
    purple: [
      '[&>summary]:text-purple',
      '[&>summary]:bg-purple-light',
      '[&>summary:focus-visible]:outline-purple'
    ],
    grey: [
      '[&>summary]:text-grey',
      '[&>summary]:bg-transparent',
      '[&>summary:focus-visible]:outline-grey'
    ]
  },
  level: {
    title: ['[&>summary]:sm:text-h3'],
    'sub-title': ['[&>summary]:sm:text-p1']
  },
  interactivity: {
    enabled: ['[&>summary]:cursor-pointer'],
    disabled: ['[&>summary]:pointer-events-none', '[&>summary]:select-none']
  }
} as const;

/* eslint-disable ember/template-no-positive-tabindex */
export default class WrDetails extends Component<S> {
  /** internal state to track wether the details is open or closed */
  @tracked _isOpen = true;

  /** track wether the <details> has been toggled or not */
  @tracked wasToggled = false;

  get isOpen() {
    if (this.wasToggled) return this._isOpen;

    return this.args.open ?? this._isOpen;
  }

  get color() {
    return this.args.color || 'purple';
  }

  get level() {
    return this.args.level || 'title';
  }

  get interactivity() {
    return this.args.disabled ? 'disabled' : 'enabled';
  }

  get styles() {
    return [
      ...STYLES.base,
      ...STYLES.color[this.color],
      ...STYLES.level[this.level],
      ...STYLES.interactivity[this.interactivity]
    ].join(' ');
  }

  get isDisabled() {
    return !!this.args.disabled;
  }

  get isEnabled() {
    return !this.isDisabled;
  }

  get arrowIcon() {
    return this.level == 'title' ? ThickArrowDownIcon : ArrowDownIcon;
  }

  /**
   * Manually Polyfill the ToggleEvent API: newState
   * See https://caniuse.com/mdn-api_toggleevent_newstate
   *
   * The browser will throw a ReferenceError error if ToggleEvent is not defined
   * And we also make sure the "newState" property exists
   */
  get browserSupportsNewStateAPI() {
    try {
      return (
        typeof ToggleEvent === 'function' && 'newState' in ToggleEvent.prototype
      );
      // TODO: pre-existing, see the eslint-plugin-ember migration ticket
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  }

  toggleOpen = (event: Event) => {
    this.wasToggled = true;

    // Let's rely on the newState API for browsers that support it
    // and implement our custom logic for older browsers
    if (this.browserSupportsNewStateAPI)
      this._isOpen = (event as ToggleEvent).newState == 'open';
    else
      this._isOpen = (event.target as HTMLDetailsElement).open ? true : false;
  };

  /**
   * A modifier to reset this.wasToggled to false after
   * an update of the @open arg by the invoker of this component
   * */
  resetWasToggled = modifier((el, [openArg]) => {
    /*
    we need to consume "openArg" to make sure this modifier is called
    when openArg is modified, even if we do not use "openArg" later on…
    */
    // TODO: pre-existing, see the eslint-plugin-ember migration ticket
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    openArg;

    // Reset this.wasToggled since the @open arg has been updated
    this.wasToggled = false;
  });

  <template>
    <details
      {{this.resetWasToggled @open}}
      {{on 'toggle' this.toggleOpen}}
      open={{this.isOpen}}
      class={{this.styles}}
      ...attributes
    >
      <summary tabindex={{if this.isDisabled '-1' false}}>

        {{! Display the arrow icon only if this WrDetails can be opened/closed }}
        {{#if this.isEnabled}}
          <this.arrowIcon
            class='shrink-0 -rotate-90 transition-transform duration-500 ease-in-out'
          />
        {{/if}}

        {{!
        The summary can either be passed as the @summary argument if it is just a string
        or as the <:summary> named block if you need more control over its content
        }}
        {{#if (has-block 'summary')}}
          {{yield this.isOpen to='summary'}}
        {{else}}
          {{@summary}}
        {{/if}}
      </summary>

      {{yield this.isOpen}}
    </details>
  </template>
}
